import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, Plus, Calendar, Trash2, Clock, Edit3, X, AlertCircle } from 'lucide-react';
import { API } from '../config';
import ExcelImportPanel from './ExcelImportPanel';

const RATE_FIELDS = [
  { key: 'power_rate_per_unit', label: 'Power Rate', unit: '₹/Unit' },
  { key: 'steam_rate_per_mt', label: 'Steam Rate', unit: '₹/MT' },
  { key: 'water_rate_per_kl', label: 'Water Rate', unit: '₹/KL' },
  { key: 'cabol_100_rate_per_l', label: 'Cabol 100 Rate', unit: '₹/Liter' },
  { key: 'freezol_68_rate_per_l', label: 'Freezol-68 Rate', unit: '₹/Liter' },
  { key: 'hydrol_100_rate_per_l', label: 'Hydrol 100 Rate', unit: '₹/Liter' },
  { key: 'hydrol_150_rate_per_l', label: 'Hydrol 150 Rate', unit: '₹/Liter' },
  { key: 'servo_320_rate_per_l', label: 'Servo 320 Rate', unit: '₹/Liter' },
  { key: 'servo_220_rate_per_l', label: 'Servo 220 Rate', unit: '₹/Liter' },
  { key: 'carbon_refill_rate_per_kg', label: 'Carbon Refill Rate', unit: '₹/Kg' },
  { key: 'alumina_balls_rate_per_kg', label: 'Alumina Balls Rate', unit: '₹/Kg' },
  { key: 'mol_sieves_13x_rate_per_kg', label: '13X Mol Sieves Rate', unit: '₹/Kg' },
  { key: 'nh3_rate_per_kg', label: 'NH₃ Rate', unit: '₹/Kg' },
  { key: 'kmno4_rate_per_kg', label: 'KMNO₄ Rate', unit: '₹/Kg' },
  { key: 'co2_purchase_rate_per_mt', label: 'CO₂ Purchase', unit: '₹/MT (0 = N/A)' },
];

const DEFAULT_RATES = {
  power_rate_per_unit: 7.0,
  steam_rate_per_mt: 1500.0,
  water_rate_per_kl: 50.0,
  cabol_100_rate_per_l: 95.0,
  freezol_68_rate_per_l: 95.0,
  hydrol_100_rate_per_l: 95.0,
  hydrol_150_rate_per_l: 95.0,
  servo_320_rate_per_l: 95.0,
  servo_220_rate_per_l: 95.0,
  carbon_refill_rate_per_kg: 45.0,
  alumina_balls_rate_per_kg: 120.0,
  mol_sieves_13x_rate_per_kg: 0,
  nh3_rate_per_kg: 35.0,
  kmno4_rate_per_kg: 80.0,
  co2_purchase_rate_per_mt: 0,
};

const RateInput = ({ label, unit, value, onChange, testid }) => (
  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 hover:border-[#156ACA] transition-colors">
    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₹</span>
      <input
        type="number" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        step="0.01" min="0"
        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA] focus:border-transparent text-base font-semibold"
        data-testid={testid}
      />
    </div>
    <p className="text-xs text-gray-500 mt-1">{unit}</p>
  </div>
);

const RateForm = ({ initialEffectiveFrom, initialRates, initialNote, onSave, onCancel, loading, submitLabel }) => {
  const [effectiveFrom, setEffectiveFrom] = useState(initialEffectiveFrom || new Date().toISOString().slice(0, 10));
  const [rates, setRates] = useState(initialRates || { ...DEFAULT_RATES });
  const [note, setNote] = useState(initialNote || '');
  const [error, setError] = useState('');

  const handleField = (k, v) => setRates(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!effectiveFrom) {
      setError('Effective-from date is required');
      return;
    }
    try {
      await onSave({ effective_from: effectiveFrom, rates, note });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save rate period');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Effective From <span className="text-red-500">*</span>
          </label>
          <input
            type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA]"
            data-testid="rate-effective-from-input"
          />
          <p className="text-xs text-gray-500 mt-1">All shifts on or after this date use these rates (until the next period).</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Note (optional)</label>
          <input
            type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g., FY26 Q1 tariff revision"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA]"
            data-testid="rate-note-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
        {RATE_FIELDS.map(f => (
          <RateInput key={f.key} label={f.label} unit={f.unit}
            value={rates[f.key]} onChange={(v) => handleField(f.key, v)}
            testid={`rate-${f.key}`} />
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-center" data-testid="rate-form-error">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-3 sm:gap-0">
        <button type="button" onClick={onCancel}
          className="px-4 py-2.5 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          data-testid="rate-form-cancel-btn">
          Cancel
        </button>
        <button type="submit" disabled={loading}
          className="bg-[#156ACA] hover:bg-[#0f56a3] text-white font-semibold px-5 py-2.5 min-h-[44px] rounded-lg disabled:opacity-50 flex items-center justify-center"
          data-testid="rate-form-submit-btn">
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : (submitLabel || 'Save Rate Period')}
        </button>
      </div>
    </form>
  );
};

const formatRupee = (n) => `₹${Number(n).toFixed(2)}`;

const RatesManagementView = ({ plants, selectedPlant, handlePlantChange, token }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [message, setMessage] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadHistory = useCallback(async () => {
    if (!selectedPlant) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/plants/${selectedPlant.id}/rate-history`, { headers });
      setHistory(res.data);
    } catch (err) {
      setMessage(`✗ Failed to load rate history: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedPlant, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSave = async (payload) => {
    setMessage('');
    setLoading(true);
    try {
      if (editingEntry) {
        await axios.put(`${API}/plants/${selectedPlant.id}/rate-history/${editingEntry.id}`, payload, { headers });
        setMessage(`✓ Rate period updated`);
      } else {
        await axios.post(`${API}/plants/${selectedPlant.id}/rate-history`, payload, { headers });
        setMessage(`✓ New rate period effective from ${payload.effective_from} added`);
      }
      setShowAddForm(false);
      setEditingEntry(null);
      await loadHistory();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete the rate period effective from ${entry.effective_from}? Historical costs computed before this change will revert to use neighbouring periods.`)) return;
    setMessage('');
    try {
      setLoading(true);
      await axios.delete(`${API}/plants/${selectedPlant.id}/rate-history/${entry.id}`, { headers });
      setMessage(`✓ Rate period deleted`);
      await loadHistory();
    } catch (err) {
      setMessage(`✗ ${err.response?.data?.detail || 'Failed to delete'}`);
    } finally {
      setLoading(false);
    }
  };

  // Mark the currently-effective period (latest entry whose effective_from <= today)
  const today = new Date().toISOString().slice(0, 10);
  const activeEntryId = history.find(e => e.effective_from <= today)?.id;

  // Default rates for a new period = the most recent entry's rates (or library defaults)
  const seedRatesForNewForm = history[0]?.rates || { ...DEFAULT_RATES };

  return (
    <div className="space-y-6">
      {/* Plant Selector */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Plant to Manage Rates
        </label>
        <select
          value={selectedPlant?.id || ''}
          onChange={(e) => handlePlantChange(e.target.value)}
          className="w-full px-4 py-3 min-h-[44px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA] text-base font-medium"
          data-testid="rates-plant-select"
        >
          {plants.map(plant => (
            <option key={plant.id} value={plant.id}>
              {plant.name} {plant.location && `(${plant.location})`}
            </option>
          ))}
        </select>
      </div>

      {/* Rate History Header + Add Button */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-[#156ACA] flex-shrink-0" />
              Rate Periods
            </h2>
            <p className="text-gray-600 mt-1 text-xs sm:text-sm">
              Costs for each shift are calculated using the rates that were effective on that shift's date.
            </p>
          </div>
          {!showAddForm && !editingEntry && (
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-[#156ACA] hover:bg-[#0f56a3] text-white font-semibold py-2.5 px-4 sm:px-5 min-h-[44px] rounded-lg shadow flex items-center whitespace-nowrap"
              data-testid="add-rate-period-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Period
            </button>
          )}
        </div>

        {message && (
          <div
            className={`mt-3 px-3 py-2 rounded-lg text-sm ${
              message.startsWith('✓')
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
            data-testid="rates-message"
          >
            {message}
          </div>
        )}
      </div>

      {/* Bulk Excel Import / Template */}
      {selectedPlant && (
        <ExcelImportPanel
          title="Bulk Import (Excel)"
          description="Backfill historical rate changes from a spreadsheet — useful for entering past financial-year tariffs in one go."
          templateUrl={`/plants/${selectedPlant.id}/rate-history/template.xlsx`}
          importUrl={`/plants/${selectedPlant.id}/rate-history/import`}
          templateFilename={`rate_history_template_${selectedPlant.name.replace(/\s+/g, '_')}.xlsx`}
          token={token}
          onImported={loadHistory}
          testidPrefix="rate-bulk"
        />
      )}

      {/* Add / Edit Form */}
      {(showAddForm || editingEntry) && (
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-[#156ACA]/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              {editingEntry ? `Edit Rate Period (${editingEntry.effective_from})` : 'Add New Rate Period'}
            </h3>
            <button onClick={() => { setShowAddForm(false); setEditingEntry(null); }}
              className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <RateForm
            initialEffectiveFrom={editingEntry?.effective_from}
            initialRates={editingEntry?.rates || seedRatesForNewForm}
            initialNote={editingEntry?.note || ''}
            onSave={handleSave}
            onCancel={() => { setShowAddForm(false); setEditingEntry(null); }}
            loading={loading}
            submitLabel={editingEntry ? 'Update Period' : 'Save Rate Period'}
          />
        </div>
      )}

      {/* Empty state */}
      {!loading && history.length === 0 && !showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No Rate Periods Yet</h3>
          <p className="text-gray-600 text-sm mb-4">Add your first rate period to start tracking time-effective costs.</p>
          <button onClick={() => setShowAddForm(true)}
            className="bg-[#156ACA] hover:bg-[#0f56a3] text-white font-semibold py-2 px-5 rounded-lg shadow inline-flex items-center"
            data-testid="empty-add-rate-period-btn">
            <Plus className="h-5 w-5 mr-2" />
            Add First Rate Period
          </button>
        </div>
      )}

      {/* Rate periods list */}
      {history.length > 0 && (
        <div className="space-y-3" data-testid="rate-history-list">
          {history.map((entry, idx) => {
            const nextEntry = history[idx - 1]; // history is sorted DESC; the visually-above entry is "next"
            const effectiveUntil = nextEntry
              ? `until ${new Date(new Date(nextEntry.effective_from).getTime() - 86400000).toISOString().slice(0, 10)}`
              : '— ongoing';
            const isActive = entry.id === activeEntryId;
            return (
              <div
                key={entry.id}
                className={`bg-white rounded-lg shadow-md p-4 sm:p-5 border-l-4 ${
                  isActive ? 'border-green-500' : 'border-gray-300'
                }`}
                data-testid={`rate-period-${entry.effective_from}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center space-x-2 mb-1 flex-wrap">
                      <Calendar className="h-5 w-5 text-[#156ACA] flex-shrink-0" />
                      <h4 className="text-base sm:text-lg font-bold text-gray-900">
                        From {entry.effective_from}
                      </h4>
                      {isActive && (
                        <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 break-words">
                      Effective {effectiveUntil}
                      {entry.note && <span className="ml-2 italic">— {entry.note}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setEditingEntry(entry); setShowAddForm(false); }}
                      className="text-[#156ACA] hover:bg-[#E8F2FC] px-3 py-2 min-h-[40px] rounded-lg flex items-center text-sm font-medium border border-[#156ACA]/30"
                      data-testid={`edit-rate-${entry.effective_from}`}>
                      <Edit3 className="h-4 w-4 mr-1" /> Edit
                    </button>
                    <button onClick={() => handleDelete(entry)}
                      className="text-red-600 hover:bg-red-50 px-3 py-2 min-h-[40px] rounded-lg flex items-center text-sm font-medium border border-red-200"
                      data-testid={`delete-rate-${entry.effective_from}`}>
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
                  {RATE_FIELDS.map(f => (
                    <div key={f.key} className="bg-gray-50 rounded px-3 py-2">
                      <div className="text-xs text-gray-500">{f.label}</div>
                      <div className="font-semibold text-gray-900">{formatRupee(entry.rates[f.key])}</div>
                      <div className="text-xs text-gray-400">{f.unit}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RatesManagementView;
